// src/admin/FormEditor/components/FormHeader/FormHeader.jsx
import { memo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AvailablePurposes,
  AvailableTemplates,
} from "../../../../constants/common.js";
import { updateFormMetadata } from "../../store/formEditorSlice";
import { sanitizeIdentifier } from "../../utils/sanitization";
import FormActions from "./FormActions";
import LanguageToggle from "./LanguageToggle";

const FormHeader = memo(() => {
  const dispatch = useDispatch();
  const formMetadata = useSelector((state) => state.formEditor.formMetadata);

  const handleFormIdChange = useCallback(
    (e) => {
      const sanitizedId = sanitizeIdentifier(e.target.value);
      const updates = { form_id: sanitizedId };
      dispatch(updateFormMetadata({ updates }));
    },
    [dispatch]
  );

  const handlePurposeChange = (e) => {
    const updates = { purpose: e.target.value };
    dispatch(updateFormMetadata({ updates }));
  };

  const handleTemplateChange = (e) => {
    const updates = { template: e.target.value };
    dispatch(updateFormMetadata({ updates }));
  };

  const handleSubformChange = (e) => {
    const updates = { subform: e.target.checked };
    dispatch(updateFormMetadata({ updates }));
  };

  return (
    <div className="space-y-2">
      {/* Form controls */}
      <div className="space-y-2">
        {/* Form ID, Subform and Actions - with wrapping */}
        <div className="flex flex-wrap items-center gap-4">
          {/* Form ID and Subform group - keeps these together */}
          <div className="flex flex-1 min-w-[300px] items-center gap-4">
            <div className="flex items-center gap-2 flex-1">
              <label
                htmlFor="form_id"
                className="text-sm font-medium text-gray-700 whitespace-nowrap"
              >
                Form ID
              </label>
              <input
                type="text"
                id="form_id"
                value={formMetadata.form_id}
                onChange={handleFormIdChange}
                className="flex-1 border rounded-md p-2"
                placeholder="form_id"
              />
            </div>

            <label className="flex items-center gap-2 flex-shrink-0">
              <input
                type="checkbox"
                checked={formMetadata.subform ?? false}
                onChange={handleSubformChange}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 whitespace-nowrap">
                Subform
              </span>
            </label>
          </div>

          {/* Form Actions - will wrap to new line when space is insufficient */}
          <div className="flex-shrink-0">
            <FormActions />
          </div>
        </div>

        {/* Purpose and Template row */}
        <div className="flex flex-wrap items-center gap-4">
          {/* Purpose section */}
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Purpose
            </label>
            <select
              value={formMetadata.purpose}
              onChange={handlePurposeChange}
              className="flex-1 border rounded-md p-2"
            >
              {AvailablePurposes.map((purpose) => (
                <option key={purpose} value={purpose}>
                  {purpose}
                </option>
              ))}
            </select>
          </div>

          {/* Template section */}
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Template
            </label>
            <select
              value={formMetadata.template}
              onChange={handleTemplateChange}
              className="flex-1 border rounded-md p-2"
              disabled={true}
            >
              {AvailableTemplates.map((template) => (
                <option key={template} value={template}>
                  {template.charAt(0).toUpperCase() + template.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <LanguageToggle />
    </div>
  );
});

export default FormHeader;
